import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Grid } from "@mui/material";
import { Chat } from "./Chat";
import ChatDivider from "./ChatDivider";
import ChatPDF from "./ChatPDF";
import React from "react";
import apiClient from "../../utils/api";
import {
  PageNavigationPlugin,
  pageNavigationPlugin,
} from "@react-pdf-viewer/page-navigation";

import ClassCardForStudent from "../AdminComponents/ClassCards/ClassCardForStudent";

interface KiwiClass {
  className?: string;
  classID: string;
  professorName?: string;
}

export const ChatContext = React.createContext<{
  class?: KiwiClass;
  currentPDFID?: string;
  setCurrentPDFID?: (pdfID: string) => void;
  currentPdfName?: string;
  setCurrentPdfName?: (pdfName: string) => void;
  currentPDFPage?: number;
  currentPDFMaterialType?: string;
  setCurrentPDFMaterialType?: (materialType: string) => void;
  setCurrentPDFPage?: (pageNumber: number) => void;
  pageNavigationPluginInstance?: PageNavigationPlugin;
  pdfContent?: string;
  setPdfContent?: (content: string) => void;
  multipleCourses?: boolean;
  engineChoice?: string;
  setEngineChoice?: (engine: string) => void;
}>({});

const AdjustableSectionView = () => {
  const [sectionRatio, setSectionRatio] = useState(45);

  // class data
  const [myClass, setMyClass] = useState<KiwiClass>();
  const [availableClasses, setAvailableClasses] = useState<KiwiClass[]>([]);
  const [isClassSelected, setIsClassSelected] = useState(false);
  const [multipleCourses, setMultipleCourses] = useState(false);

  // chat engine
  const [engineChoice, setEngineChoice] = useState(
    localStorage.getItem("engineChoice") || "openai"
  );

  // pdf data
  const [currentPDFID, setCurrentPDFID] = useState<string>("");
  const [pdfContent, setPdfContent] = useState<string>("");
  const [currentPdfName, setCurrentPdfName] = useState<string>("");
  const [currentPDFPage, setCurrentPDFPage] = useState<number>(0);
  const [currentPDFMaterialType, setCurrentPDFMaterialType] =
    useState<string>("");
  const pageNavigationPluginInstance = pageNavigationPlugin();

  // useSearchParams to handle query params for classID
  const [searchParams, setSearchParams] = useSearchParams();

  const classIDFromQuery = searchParams.get("classID");

  useEffect(() => {
    const fetchEnrolledClasses = async () => {
      const { enrollments } =
        await apiClient.studentControllerGetEnrolledClasses();
      if (!enrollments) {
        return;
      }
      // get the classIds from the enrollments
      const enrolledClassIds = enrollments.map(
        (enrollment) => enrollment.classId
      );

      // fetch the default class when no class available
      if (enrolledClassIds.length === 0) {
        const { classIds } = await apiClient.classControllerList();
        enrolledClassIds.push(classIds[0]);
      }

      if (enrolledClassIds.length > 1) {
        setMultipleCourses(true);
      }
      const fetchedClasses = await Promise.all(
        enrolledClassIds.map(async (id) => {
          const classEntry = await apiClient.classControllerGetById({
            classID: id,
          });
          const classMetadataJson = JSON.parse(classEntry.classMetadata);
          return {
            classID: classEntry.id,
            className: classMetadataJson.className,
            professorName: classMetadataJson.professorName,
            professorEmail: classMetadataJson.professorEmail,
          };
        })
      );

      if (classIDFromQuery && enrolledClassIds.includes(classIDFromQuery)) {
        // If classID is present in the URL, load that class
        const selectedClass = enrolledClassIds.includes(classIDFromQuery)
          ? fetchedClasses.find((item) => item.classID === classIDFromQuery)
          : null;
        if (selectedClass) {
          setMyClass(selectedClass);
          setIsClassSelected(true);
        }
      } else if (enrolledClassIds.length === 1) {
        setMyClass(fetchedClasses[0]);
        setIsClassSelected(true);
        setSearchParams({ classID: enrolledClassIds[0] });
      } else {
        setAvailableClasses(fetchedClasses);
      }
    };
    fetchEnrolledClasses();
  }, [classIDFromQuery, setSearchParams]);

  const handleClassSelection = (selectedClass: KiwiClass) => {
    setMyClass(selectedClass);
    setIsClassSelected(true);
    // Update the URL path with the selected classID
    setSearchParams({ classID: selectedClass.classID });
  };

  const contextData = {
    class: myClass,
    currentPDFID,
    setCurrentPDFID,
    currentPdfName,
    setCurrentPdfName,
    currentPDFPage,
    setCurrentPDFPage,
    currentPDFMaterialType,
    setCurrentPDFMaterialType,
    pageNavigationPluginInstance,
    pdfContent,
    setPdfContent,
    multipleCourses,
    engineChoice,
    setEngineChoice,
  };

  return (
    <ChatContext.Provider value={contextData}>
      <Grid container direction="row" minWidth="700px">
        {!isClassSelected && availableClasses.length > 0 ? (
          <Grid item xs={12}>
            <Box>
              <h2>Select a Class</h2>
              <Grid container spacing={2}>
                {availableClasses.map((classInfo) => (
                  <Grid item xs={12} sm={6} md={4} key={classInfo.classID}>
                    <ClassCardForStudent
                      classID={classInfo.classID}
                      className={classInfo.className || "Class"}
                      onClick={() => handleClassSelection(classInfo)}
                      professorName={classInfo.professorName || "TBD"}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
        ) : !isClassSelected && availableClasses.length === 0 ? (
          // Render this when no classes are available and no class is selected
          <Grid item xs={12}>
            <Box>
              <h2>No Class enrolled</h2>
            </Box>
          </Grid>
        ) : (
          <>
            <Grid item xs="auto" height="calc(100vh - 600px)">
              <Box width={`${sectionRatio}vw`} minWidth="300px">
                <ChatPDF />
              </Box>
            </Grid>
            <ChatDivider setSectionRatio={setSectionRatio} />
            <Grid item xs minWidth="400px">
              <Chat />
            </Grid>
          </>
        )}
      </Grid>
    </ChatContext.Provider>
  );
};

export default AdjustableSectionView;
